import { useState, useMemo, useCallback, useEffect } from "react";
import {
  DollarSign,
  Search,
  TrendingUp,
  TrendingDown,
  Minus,
  Calculator,
  Loader2,
  Trash2,
  Clock,
  BarChart3,
  Target,
  Zap,
} from "lucide-react";

const API = (import.meta.env.VITE_API_URL || "") + "/api";

function getToken() {
  return localStorage.getItem("erc_token");
}

interface CompetitorData {
  priceRange: { min: number; max: number; avg: number };
  popularTags: string[];
  competitorCount?: number;
}

interface FeeBreakdown {
  listingFee: number;
  transactionFee: number;
  paymentProcessing: number;
  shippingCost: number;
  totalFees: number;
  netProfit: number;
  profitMargin: number;
  roi: number;
}

interface SavedCalculation {
  id: string;
  keyword: string;
  costPrice: number;
  sellingPrice: number;
  shippingCost: number;
  netProfit: number;
  profitMargin: number;
  date: string;
}

const STORAGE_KEY = "erc_pricing_history";

function loadSavedCalcs(): SavedCalculation[] {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
  } catch {
    return [];
  }
}

function saveCals(calcs: SavedCalculation[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(calcs.slice(0, 20)));
}

function calculateFees(sellingPrice: number, costPrice: number, shippingCost: number): FeeBreakdown {
  const listingFee = 0.2;
  const transactionFee = sellingPrice * 0.065;
  const paymentProcessing = sellingPrice * 0.03 + 0.25;
  const totalFees = listingFee + transactionFee + paymentProcessing + shippingCost;
  const netProfit = sellingPrice - costPrice - totalFees;
  const profitMargin = sellingPrice > 0 ? (netProfit / sellingPrice) * 100 : 0;
  const roi = costPrice > 0 ? (netProfit / costPrice) * 100 : 0;
  return { listingFee, transactionFee, paymentProcessing, shippingCost, totalFees, netProfit, profitMargin, roi };
}

const CATEGORIES = [
  "All Categories",
  "Accessories",
  "Art & Collectibles",
  "Bags & Purses",
  "Bath & Beauty",
  "Books, Movies & Music",
  "Clothing",
  "Craft Supplies & Tools",
  "Electronics & Accessories",
  "Gifts",
  "Home & Living",
  "Jewelry",
  "Paper & Party Supplies",
  "Pet Supplies",
  "Shoes",
  "Toys & Games",
  "Weddings",
];

export default function PricingAssistantPage() {
  const [keyword, setKeyword] = useState("");
  const [category, setCategory] = useState("All Categories");
  const [costPrice, setCostPrice] = useState("");
  const [targetMargin, setTargetMargin] = useState("30");
  const [sellingPrice, setSellingPrice] = useState("");
  const [shippingCost, setShippingCost] = useState("0");
  const [competitorData, setCompetitorData] = useState<CompetitorData | null>(null);
  const [loading, setLoading] = useState(false);
  const [savedCalcs, setSavedCalcs] = useState<SavedCalculation[]>(loadSavedCalcs);

  const fees = useMemo(() => {
    const sp = parseFloat(sellingPrice) || 0;
    const cp = parseFloat(costPrice) || 0;
    const sc = parseFloat(shippingCost) || 0;
    return calculateFees(sp, cp, sc);
  }, [sellingPrice, costPrice, shippingCost]);

  const pricingStrategies = useMemo(() => {
    if (!competitorData) return null;
    const { min, max, avg } = competitorData.priceRange;
    const cp = parseFloat(costPrice) || 0;
    const sc = parseFloat(shippingCost) || 0;

    const budget = Math.max(min, avg * 0.8);
    const competitive = avg;
    const premium = Math.min(max, avg * 1.3);

    return [
      {
        name: "Budget",
        description: "Below average - higher volume potential",
        price: budget,
        fees: calculateFees(budget, cp, sc),
        icon: TrendingDown,
        color: "border-blue-200 bg-blue-50",
        textColor: "text-blue-700",
      },
      {
        name: "Competitive",
        description: "At market average - balanced approach",
        price: competitive,
        fees: calculateFees(competitive, cp, sc),
        icon: Minus,
        color: "border-emerald-200 bg-emerald-50",
        textColor: "text-emerald-700",
      },
      {
        name: "Premium",
        description: "Above average - higher margin focus",
        price: premium,
        fees: calculateFees(premium, cp, sc),
        icon: TrendingUp,
        color: "border-purple-200 bg-purple-50",
        textColor: "text-purple-700",
      },
    ];
  }, [competitorData, costPrice, shippingCost]);

  const fetchCompetitorData = useCallback(async () => {
    if (!keyword.trim()) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/listing-builder/seo-generate`, {
        method: "POST",
        headers: { Authorization: `Bearer ${getToken()}`, "Content-Type": "application/json" },
        body: JSON.stringify({ keyword: keyword.trim(), category }),
      });
      if (!res.ok) throw new Error("Failed to fetch competitor data");
      const data = await res.json();
      const result: CompetitorData = {
        priceRange: data.priceRange ?? data.data?.priceRange ?? { min: 5, max: 50, avg: 25 },
        popularTags: data.popularTags ?? data.data?.popularTags ?? [],
        competitorCount: data.competitorCount ?? data.data?.competitorCount,
      };
      setCompetitorData(result);
      setSellingPrice(result.priceRange.avg.toFixed(2));
    } catch {
      alert("Failed to fetch competitor pricing data.");
    } finally {
      setLoading(false);
    }
  }, [keyword, category]);

  // Auto-fill selling price from target margin
  useEffect(() => {
    const cp = parseFloat(costPrice);
    const tm = parseFloat(targetMargin);
    if (cp > 0 && tm > 0 && !competitorData) {
      const estimated = cp / (1 - tm / 100);
      setSellingPrice(estimated.toFixed(2));
    }
  }, [costPrice, targetMargin, competitorData]);

  function handleSaveCalculation() {
    const sp = parseFloat(sellingPrice) || 0;
    const cp = parseFloat(costPrice) || 0;
    if (sp <= 0) return;
    const calc: SavedCalculation = {
      id: Date.now().toString(),
      keyword: keyword || "Manual",
      costPrice: cp,
      sellingPrice: sp,
      shippingCost: parseFloat(shippingCost) || 0,
      netProfit: fees.netProfit,
      profitMargin: fees.profitMargin,
      date: new Date().toISOString(),
    };
    const updated = [calc, ...savedCalcs].slice(0, 20);
    setSavedCalcs(updated);
    saveCals(updated);
  }

  function handleDeleteCalc(id: string) {
    const updated = savedCalcs.filter((c) => c.id !== id);
    setSavedCalcs(updated);
    saveCals(updated);
  }

  function handleLoadCalc(calc: SavedCalculation) {
    setKeyword(calc.keyword === "Manual" ? "" : calc.keyword);
    setCostPrice(String(calc.costPrice));
    setSellingPrice(String(calc.sellingPrice));
    setShippingCost(String(calc.shippingCost));
  }

  return (
    <div className="mx-auto max-w-6xl p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <DollarSign className="h-6 w-6 text-emerald-500" />
          Pricing Assistant
        </h1>
        <p className="mt-1 text-sm text-gray-500">
          Analyze competitor pricing and calculate optimal prices for your Etsy listings
        </p>
      </div>

      <div className="grid grid-cols-3 gap-6">
        {/* Left Column - Input & Competitor Analysis */}
        <div className="col-span-2 space-y-6">
          {/* Input Section */}
          <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            <h2 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Search className="h-4 w-4 text-gray-400" />
              Market Research
            </h2>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Product Keyword</label>
                <input
                  type="text"
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && fetchCompetitorData()}
                  placeholder="e.g. custom mug, personalized gift"
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-indigo-300 focus:outline-none focus:ring-1 focus:ring-indigo-300"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Category</label>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-indigo-300 focus:outline-none focus:ring-1 focus:ring-indigo-300"
                >
                  {CATEGORIES.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Cost Price ($)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={costPrice}
                  onChange={(e) => setCostPrice(e.target.value)}
                  placeholder="0.00"
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-indigo-300 focus:outline-none focus:ring-1 focus:ring-indigo-300"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Target Profit Margin (%)</label>
                <input
                  type="number"
                  min="0"
                  max="99"
                  value={targetMargin}
                  onChange={(e) => setTargetMargin(e.target.value)}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-indigo-300 focus:outline-none focus:ring-1 focus:ring-indigo-300"
                />
              </div>
            </div>
            <button
              onClick={fetchCompetitorData}
              disabled={!keyword.trim() || loading}
              className="flex items-center gap-2 rounded-lg bg-emerald-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50 transition-colors"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <BarChart3 className="h-4 w-4" />}
              Analyze Competitors
            </button>
          </div>

          {/* Competitor Analysis Results */}
          {competitorData && (
            <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
              <h2 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <BarChart3 className="h-4 w-4 text-indigo-500" />
                Competitor Price Analysis
                {competitorData.competitorCount != null && (
                  <span className="text-xs font-normal text-gray-400">
                    ({competitorData.competitorCount} competitors)
                  </span>
                )}
              </h2>

              {/* Price Range Bar */}
              <div className="mb-5">
                <div className="flex justify-between text-xs text-gray-500 mb-1">
                  <span>${competitorData.priceRange.min.toFixed(2)}</span>
                  <span className="font-medium text-indigo-600">
                    Avg: ${competitorData.priceRange.avg.toFixed(2)}
                  </span>
                  <span>${competitorData.priceRange.max.toFixed(2)}</span>
                </div>
                <div className="relative h-6 rounded-full bg-gray-100 overflow-hidden">
                  <div className="absolute inset-0 rounded-full bg-gradient-to-r from-blue-200 via-emerald-200 to-purple-200" />
                  {/* Average marker */}
                  <div
                    className="absolute top-0 bottom-0 w-0.5 bg-indigo-600"
                    style={{
                      left: `${
                        ((competitorData.priceRange.avg - competitorData.priceRange.min) /
                          (competitorData.priceRange.max - competitorData.priceRange.min)) *
                        100
                      }%`,
                    }}
                  />
                  <div
                    className="absolute -top-5 transform -translate-x-1/2"
                    style={{
                      left: `${
                        ((competitorData.priceRange.avg - competitorData.priceRange.min) /
                          (competitorData.priceRange.max - competitorData.priceRange.min)) *
                        100
                      }%`,
                    }}
                  >
                    <div className="w-3 h-3 bg-indigo-600 rotate-45 transform translate-y-3" />
                  </div>
                </div>
                <div className="flex justify-between text-[10px] text-gray-400 mt-1">
                  <span>Min</span>
                  <span>Max</span>
                </div>
              </div>

              {/* Popular Tags */}
              {competitorData.popularTags.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-gray-500 mb-2">Popular Tags</p>
                  <div className="flex flex-wrap gap-1">
                    {competitorData.popularTags.map((tag) => (
                      <span key={tag} className="rounded-full bg-gray-100 px-2.5 py-0.5 text-xs text-gray-600">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Price Suggestions */}
          {pricingStrategies && (
            <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
              <h2 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Target className="h-4 w-4 text-purple-500" />
                Pricing Strategies
              </h2>
              <div className="grid grid-cols-3 gap-4">
                {pricingStrategies.map((strategy) => {
                  const Icon = strategy.icon;
                  return (
                    <div
                      key={strategy.name}
                      className={`rounded-xl border p-4 ${strategy.color} cursor-pointer hover:opacity-90 transition-opacity`}
                      onClick={() => setSellingPrice(strategy.price.toFixed(2))}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <Icon className={`h-4 w-4 ${strategy.textColor}`} />
                        <span className={`text-sm font-semibold ${strategy.textColor}`}>{strategy.name}</span>
                      </div>
                      <p className="text-xs text-gray-600 mb-3">{strategy.description}</p>
                      <div className={`text-2xl font-bold ${strategy.textColor} mb-2`}>
                        ${strategy.price.toFixed(2)}
                      </div>
                      <div className="space-y-1 text-xs text-gray-600">
                        <div className="flex justify-between">
                          <span>Net Profit:</span>
                          <span className={`font-medium ${strategy.fees.netProfit >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                            ${strategy.fees.netProfit.toFixed(2)}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span>Margin:</span>
                          <span className="font-medium">{strategy.fees.profitMargin.toFixed(1)}%</span>
                        </div>
                        <div className="flex justify-between">
                          <span>ROI:</span>
                          <span className="font-medium">{strategy.fees.roi.toFixed(1)}%</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Right Column - Profit Calculator & Saved */}
        <div className="space-y-6">
          {/* Profit Calculator */}
          <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            <h2 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Calculator className="h-4 w-4 text-orange-500" />
              Profit Calculator
            </h2>

            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Selling Price ($)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={sellingPrice}
                  onChange={(e) => setSellingPrice(e.target.value)}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-indigo-300 focus:outline-none focus:ring-1 focus:ring-indigo-300"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Shipping Cost ($)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={shippingCost}
                  onChange={(e) => setShippingCost(e.target.value)}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-indigo-300 focus:outline-none focus:ring-1 focus:ring-indigo-300"
                />
              </div>
            </div>

            {/* Fee Breakdown */}
            <div className="mt-4 space-y-2 border-t border-gray-100 pt-4">
              <div className="flex justify-between text-xs">
                <span className="text-gray-500">Listing Fee</span>
                <span className="text-gray-700">${fees.listingFee.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-gray-500">Transaction Fee (6.5%)</span>
                <span className="text-gray-700">${fees.transactionFee.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-gray-500">Payment Processing (3% + $0.25)</span>
                <span className="text-gray-700">${fees.paymentProcessing.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-gray-500">Shipping</span>
                <span className="text-gray-700">${fees.shippingCost.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-xs font-medium border-t border-gray-100 pt-2">
                <span className="text-gray-700">Total Fees</span>
                <span className="text-red-600">${fees.totalFees.toFixed(2)}</span>
              </div>
            </div>

            {/* Results */}
            <div className="mt-4 space-y-3 border-t border-gray-100 pt-4">
              <div className="rounded-lg bg-gray-50 p-3 text-center">
                <p className="text-xs text-gray-500 mb-1">Net Profit</p>
                <p className={`text-xl font-bold ${fees.netProfit >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                  ${fees.netProfit.toFixed(2)}
                </p>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="rounded-lg bg-gray-50 p-3 text-center">
                  <p className="text-xs text-gray-500 mb-1">Margin</p>
                  <p className={`text-lg font-bold ${fees.profitMargin >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                    {fees.profitMargin.toFixed(1)}%
                  </p>
                </div>
                <div className="rounded-lg bg-gray-50 p-3 text-center">
                  <p className="text-xs text-gray-500 mb-1">ROI</p>
                  <p className={`text-lg font-bold ${fees.roi >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                    {fees.roi.toFixed(1)}%
                  </p>
                </div>
              </div>

              <button
                onClick={handleSaveCalculation}
                disabled={!sellingPrice || parseFloat(sellingPrice) <= 0}
                className="w-full flex items-center justify-center gap-2 rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-2 text-sm font-medium text-indigo-700 hover:bg-indigo-100 disabled:opacity-50 transition-colors"
              >
                <Zap className="h-3.5 w-3.5" />
                Save Calculation
              </button>
            </div>
          </div>

          {/* Saved Calculations */}
          <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            <h2 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <Clock className="h-4 w-4 text-gray-400" />
              Recent Calculations
            </h2>
            {savedCalcs.length === 0 ? (
              <p className="text-xs text-gray-400 text-center py-4">No saved calculations yet</p>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {savedCalcs.map((calc) => (
                  <div
                    key={calc.id}
                    className="flex items-center gap-2 rounded-lg border border-gray-100 p-2.5 hover:bg-gray-50 cursor-pointer group"
                    onClick={() => handleLoadCalc(calc)}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-gray-700 truncate">{calc.keyword}</p>
                      <div className="flex items-center gap-2 text-[10px] text-gray-400 mt-0.5">
                        <span>${calc.sellingPrice.toFixed(2)}</span>
                        <span className={calc.netProfit >= 0 ? "text-emerald-500" : "text-red-500"}>
                          ${calc.netProfit.toFixed(2)} ({calc.profitMargin.toFixed(1)}%)
                        </span>
                      </div>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteCalc(calc.id);
                      }}
                      className="opacity-0 group-hover:opacity-100 text-gray-300 hover:text-red-500 transition-all"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
